import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { ChartContainer } from '@/components/ui/chart';

interface RiskData {
  bins: {
    low: number;
    medium: number;
    high: number;
  };
  top_engagements: Array<{
    id: string;
    client: string;
    year: number;
    risk_score: number;
    open_findings: number;
    days_left: number | null;
  }>;
}

export function RiskOverviewWidget() {
  const { t } = useTranslation();
  const [data, setData] = useState<RiskData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRiskData();
  }, []);

  const fetchRiskData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('risk-summary', {
        body: { months: 12 }
      });
      
      if (error) throw error;
      setData(data);
    } catch (error) {
      console.error('Error fetching risk data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.risk.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 animate-pulse bg-muted rounded"></div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.risk.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{t('dashboard.risk.noEngagements')}</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = [
    { name: t('dashboard.risk.low'), value: data.bins.low, fill: 'hsl(var(--success))' },
    { name: t('dashboard.risk.medium'), value: data.bins.medium, fill: 'hsl(var(--warning))' },
    { name: t('dashboard.risk.high'), value: data.bins.high, fill: 'hsl(var(--destructive))' }
  ];

  const chartConfig = {
    value: {
      label: t('dashboard.risk.title'),
    },
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('dashboard.risk.title')}</CardTitle>
        <Button variant="outline" size="sm">
          {t('dashboard.risk.viewAll')}
        </Button>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-32 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
        
        {data.top_engagements.length > 0 && (
          <div className="mt-4">
            <h4 className="font-semibold mb-2">Top 10 {t('dashboard.risk.riskLevel')}</h4>
            <div className="space-y-2">
              {data.top_engagements.slice(0, 3).map((engagement) => (
                <div key={engagement.id} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{engagement.client}</span>
                  <span className="text-muted-foreground">
                    {engagement.risk_score} • {engagement.open_findings} {t('dashboard.risk.openFindings')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}